import { defineStore } from 'pinia';
import { ref, computed, readonly } from 'vue';
import { useFormBuilderStore } from './form-builder.store';
import {
  getMergedCellBounds,
  expandRangeToMergeAwareRectangle,
  getFirstNonEmptyCell,
} from '../lib/cell-merge-algorithm';
import {
  isRowFullySelected,
  isColumnFullySelected,
  isAllCellsSelected as isAllCellsSelectedUtil,
} from '../lib/table-selection-utils';
import { TABLE_CONSTRAINTS } from '../lib/table-constants';
import type {
  TableCell,
  FormElement,
  TableElement,
  BorderSide,
  CellProperties,
  MergedCell,
} from './form.types';

interface SelectedCell {
  row: number;
  col: number;
}

interface CellFootprint {
  startRow: number;
  endRow: number;
  startCol: number;
  endCol: number;
}

interface UpdateElementOptions {
  trackHistory?: boolean;
}

type BorderSideType = 'top' | 'right' | 'bottom' | 'left';

const ALL_SIDES: BorderSideType[] = ['top', 'right', 'bottom', 'left'];

const oppositeSide: Record<BorderSideType, BorderSideType> = {
  right: 'left',
  left: 'right',
  bottom: 'top',
  top: 'bottom',
};

function getCellFootprint(
  cells: TableCell[][],
  row: number,
  col: number,
  mergedCells: MergedCell[]
): CellFootprint {
  const cell = cells[row]?.[col];
  if (!cell?.mergeGroupId) {
    return { startRow: row, endRow: row, startCol: col, endCol: col };
  }
  const merge = mergedCells.find((m) => m.id === cell.mergeGroupId);
  if (!merge) {
    return { startRow: row, endRow: row, startCol: col, endCol: col };
  }
  return {
    startRow: merge.startRow,
    endRow: merge.endRow,
    startCol: merge.startCol,
    endCol: merge.endCol,
  };
}

function getEdgeNeighborFootprints(
  cells: TableCell[][],
  footprint: CellFootprint,
  side: BorderSideType,
  totalRows: number,
  totalCols: number,
  mergedCells: MergedCell[]
): CellFootprint[] {
  const result: CellFootprint[] = [];
  const seen = new Set<string>();

  const collect = (row: number, col: number) => {
    if (row < 0 || row >= totalRows || col < 0 || col >= totalCols) return;
    if (!cells[row]?.[col]) return;
    const fp = getCellFootprint(cells, row, col, mergedCells);
    const key = `${fp.startRow},${fp.startCol},${fp.endRow},${fp.endCol}`;
    if (seen.has(key)) return;
    seen.add(key);
    result.push(fp);
  };

  if (side === 'top') {
    const adjRow = footprint.startRow - 1;
    for (let c = footprint.startCol; c <= footprint.endCol; c++) collect(adjRow, c);
  } else if (side === 'bottom') {
    const adjRow = footprint.endRow + 1;
    for (let c = footprint.startCol; c <= footprint.endCol; c++) collect(adjRow, c);
  } else if (side === 'left') {
    const adjCol = footprint.startCol - 1;
    for (let r = footprint.startRow; r <= footprint.endRow; r++) collect(r, adjCol);
  } else if (side === 'right') {
    const adjCol = footprint.endCol + 1;
    for (let r = footprint.startRow; r <= footprint.endRow; r++) collect(r, adjCol);
  }

  return result;
}

function writeBorderSide(
  cells: TableCell[][],
  row: number,
  col: number,
  side: BorderSideType,
  value: BorderSide | undefined
): void {
  const cell = cells[row]?.[col];
  if (!cell) return;

  const currentBorder = cell.properties?.border ?? {};
  const updatedBorder: NonNullable<CellProperties['border']> = { ...currentBorder };

  if (value === undefined) {
    delete updatedBorder[side];
  } else {
    updatedBorder[side] = value;
  }

  const hasAnyBorder = ALL_SIDES.some((key) => {
    const borderSide = updatedBorder[key];
    return !!borderSide && (borderSide.width ?? 0) > 0 && borderSide.style !== 'none';
  });

  cells[row] = [...cells[row]];
  if (hasAnyBorder) {
    cells[row][col] = {
      ...cell,
      properties: {
        ...cell.properties,
        border: updatedBorder,
      },
    };
  } else {
    const cellProperties = { ...(cell.properties ?? {}) };
    delete cellProperties.border;
    cells[row][col] = {
      ...cell,
      properties: cellProperties,
    };
  }
}

function writeFootprintSide(
  cells: TableCell[][],
  footprint: CellFootprint,
  side: BorderSideType,
  value: BorderSide | undefined
): void {
  for (let r = footprint.startRow; r <= footprint.endRow; r++) {
    for (let c = footprint.startCol; c <= footprint.endCol; c++) {
      writeBorderSide(cells, r, c, side, value);
    }
  }
}

function applyBorderEdit(
  cells: TableCell[][],
  row: number,
  col: number,
  side: BorderSideType,
  value: BorderSide | undefined,
  mergedCells: MergedCell[],
  totalRows: number,
  totalCols: number,
  excludeMirrorKeys?: Set<string>
): void {
  if (!cells[row]?.[col]) return;

  const sourceFp = getCellFootprint(cells, row, col, mergedCells);
  writeFootprintSide(cells, sourceFp, side, value);

  const neighborFps = getEdgeNeighborFootprints(
    cells,
    sourceFp,
    side,
    totalRows,
    totalCols,
    mergedCells
  );
  const oppSide = oppositeSide[side];

  for (const fp of neighborFps) {
    if (excludeMirrorKeys && footprintTouchesExcluded(fp, excludeMirrorKeys)) continue;
    writeFootprintSide(cells, fp, oppSide, value);
  }
}

function footprintTouchesExcluded(fp: CellFootprint, exclude: Set<string>): boolean {
  for (let r = fp.startRow; r <= fp.endRow; r++) {
    for (let c = fp.startCol; c <= fp.endCol; c++) {
      if (exclude.has(`${r},${c}`)) return true;
    }
  }
  return false;
}

export const useTableEditorStore = defineStore('table-editor', () => {
  const formBuilderStore = useFormBuilderStore();

  const activeTableId = ref<string | null>(null);
  const selectedCells = ref<SelectedCell[]>([]);
  const anchorCell = ref<SelectedCell | null>(null);
  const activeCell = ref<SelectedCell | null>(null);

  const isEditingMode = computed(() => activeTableId.value !== null);

  const selectedCell = computed(() =>
    selectedCells.value.length === 1 ? selectedCells.value[0] : null
  );

  const activeTable = computed((): TableElement | null => {
    if (!activeTableId.value) return null;
    const element = formBuilderStore.elements.find((el) => el.id === activeTableId.value) as
      | TableElement
      | undefined;
    return element ?? null;
  });

  const selectedCellData = computed((): TableCell | null => {
    const cell = selectedCell.value;
    if (!activeTable.value || !cell) return null;
    const { row, col } = cell;
    if (!activeTable.value.properties.cells?.[row]?.[col]) {
      return null;
    }
    return activeTable.value.properties.cells[row][col];
  });

  const selectedCellsData = computed((): TableCell[] => {
    if (!activeTable.value || selectedCells.value.length === 0) return [];
    return selectedCells.value
      .map(({ row, col }) => activeTable.value!.properties.cells?.[row]?.[col])
      .filter((cell): cell is TableCell => cell !== undefined);
  });

  const hasMultipleSelection = computed(() => selectedCells.value.length > 1);

  function enterEditingMode(tableId: string) {
    activeTableId.value = tableId;
    selectedCells.value = [];
    anchorCell.value = null;
    activeCell.value = null;
  }

  function exitEditingMode() {
    activeTableId.value = null;
    selectedCells.value = [];
    anchorCell.value = null;
    activeCell.value = null;
  }

  // Helper function to find all merge groups in selection
  function findMergeGroupsInSelection(cells: SelectedCell[]): Set<string> {
    const mergeGroups = new Set<string>();

    if (!activeTable.value) return mergeGroups;

    for (const { row, col } of cells) {
      const cell = activeTable.value.properties.cells[row]?.[col];
      if (cell?.mergeGroupId) {
        mergeGroups.add(cell.mergeGroupId);
      }
    }

    return mergeGroups;
  }

  // Helper function to add cells from a merge group
  function addMergeGroupCells(
    mergeGroupId: string,
    expandedCells: Map<string, SelectedCell>
  ): void {
    if (!activeTable.value) return;

    const mergedCells = activeTable.value.properties.mergedCells ?? [];
    const mergeGroup = mergedCells.find((m) => m.id === mergeGroupId);

    if (!mergeGroup) return;

    for (let row = mergeGroup.startRow; row <= mergeGroup.endRow; row++) {
      for (let col = mergeGroup.startCol; col <= mergeGroup.endCol; col++) {
        const key = `${row},${col}`;
        if (!expandedCells.has(key)) {
          expandedCells.set(key, { row, col });
        }
      }
    }
  }

  // Helper function to find root cell (top-left) of a merge group
  function getRootCellOfMergeGroup(row: number, col: number): SelectedCell | null {
    if (!activeTable.value) return null;

    const cell = activeTable.value.properties.cells[row]?.[col];
    if (!cell?.mergeGroupId) return null;

    const bounds = getMergedCellBounds(cell.mergeGroupId, activeTable.value);
    if (!bounds) return null;

    return { row: bounds.startRow, col: bounds.startCol };
  }

  // Helper function to expand selection to include all cells from merge groups
  function expandSelectionForMergeGroups(cells: SelectedCell[]): SelectedCell[] {
    if (!activeTable.value || cells.length === 0) return cells;

    const mergeGroupsToExpand = findMergeGroupsInSelection(cells);

    if (mergeGroupsToExpand.size === 0) return cells;

    // Expand selection to include all cells from these merge groups
    const expandedCells = new Map<string, SelectedCell>();

    // Add original cells
    for (const cell of cells) {
      const key = `${cell.row},${cell.col}`;
      expandedCells.set(key, cell);
    }

    // Add all cells from merge groups
    for (const mergeGroupId of mergeGroupsToExpand) {
      addMergeGroupCells(mergeGroupId, expandedCells);
    }

    return Array.from(expandedCells.values());
  }

  function selectCell(
    row: number,
    col: number,
    additive: boolean = false,
    shiftKey: boolean = false
  ) {
    if (!activeTable.value) return;

    const maxRow = activeTable.value.properties.rows - 1;
    const maxCol = activeTable.value.properties.columns - 1;

    if (row < 0 || row > maxRow || col < 0 || col > maxCol) {
      return;
    }

    // Handle shift-click: select range from anchor to clicked cell
    if (shiftKey) {
      if (anchorCell.value) {
        // Extend selection from anchor to clicked cell
        selectCellRange(anchorCell.value.row, anchorCell.value.col, row, col);
      } else {
        // No anchor yet, just select this cell and set it as anchor
        selectedCells.value = expandSelectionForMergeGroups([{ row, col }]);
        anchorCell.value = { row, col };
        activeCell.value = { row, col };
      }
      return;
    }

    if (additive) {
      const existingIndex = selectedCells.value.findIndex(
        (cell) => cell.row === row && cell.col === col
      );

      if (existingIndex >= 0) {
        selectedCells.value = selectedCells.value.filter((_, i) => i !== existingIndex);
      } else {
        const newSelection = [...selectedCells.value, { row, col }];
        selectedCells.value = expandSelectionForMergeGroups(newSelection);
      }
      anchorCell.value = { row, col };
      activeCell.value = { row, col };
    } else {
      const rootCell = getRootCellOfMergeGroup(row, col);
      if (rootCell) {
        selectedCells.value = [rootCell];
        anchorCell.value = rootCell;
        activeCell.value = { row, col };
      } else {
        selectedCells.value = expandSelectionForMergeGroups([{ row, col }]);
        anchorCell.value = { row, col };
        activeCell.value = { row, col };
      }
    }
  }

  function selectCellRange(startRow: number, startCol: number, endRow: number, endCol: number) {
    if (!activeTable.value) return;

    const minRow = Math.min(startRow, endRow);
    const maxRow = Math.max(startRow, endRow);
    const minCol = Math.min(startCol, endCol);
    const maxCol = Math.max(startCol, endCol);

    const cells: SelectedCell[] = [];
    for (let row = minRow; row <= maxRow; row++) {
      for (let col = minCol; col <= maxCol; col++) {
        if (
          row >= 0 &&
          row < activeTable.value.properties.rows &&
          col >= 0 &&
          col < activeTable.value.properties.columns
        ) {
          cells.push({ row, col });
        }
      }
    }

    // Snap the range to the smallest rectangle that fully encloses every merge
    // group it touches, so uneven neighbouring merges still form a mergeable block
    selectedCells.value = expandRangeToMergeAwareRectangle(cells, activeTable.value);
    // Keep anchor at the start of the range for future shift-clicks
    if (cells.length > 0) {
      anchorCell.value = { row: startRow, col: startCol };
      activeCell.value = { row: endRow, col: endCol };
    }
  }

  function clearCellSelection() {
    selectedCells.value = [];
    anchorCell.value = null;
    activeCell.value = null;
  }

  function isRowSelected(rowIndex: number): boolean {
    if (!activeTable.value) return false;
    return isRowFullySelected(
      activeTable.value.properties.cells,
      selectedCells.value,
      rowIndex,
      activeTable.value.properties.columns
    );
  }

  function isColumnSelected(colIndex: number): boolean {
    if (!activeTable.value) return false;
    return isColumnFullySelected(
      activeTable.value.properties.cells,
      selectedCells.value,
      colIndex,
      activeTable.value.properties.rows
    );
  }

  function isAllCellsSelected(): boolean {
    if (!activeTable.value) return false;
    return isAllCellsSelectedUtil(activeTable.value.properties.cells, selectedCells.value);
  }

  function selectRow(rowIndex: number) {
    if (!activeTable.value) return;

    if (isRowSelected(rowIndex)) {
      clearCellSelection();
      return;
    }

    const columns = activeTable.value.properties.columns;
    const cells: SelectedCell[] = [];

    for (let col = 0; col < columns; col++) {
      const cell = activeTable.value.properties.cells[rowIndex]?.[col];
      if (cell?.isMerged) continue;
      cells.push({ row: rowIndex, col });
    }

    selectedCells.value = expandSelectionForMergeGroups(cells);
    anchorCell.value = cells[0] ?? null;
    activeCell.value = cells[cells.length - 1] ?? null;
  }

  function selectColumn(colIndex: number) {
    if (!activeTable.value) return;

    if (isColumnSelected(colIndex)) {
      clearCellSelection();
      return;
    }

    const rows = activeTable.value.properties.rows;
    const cells: SelectedCell[] = [];

    for (let row = 0; row < rows; row++) {
      const cell = activeTable.value.properties.cells[row]?.[colIndex];
      if (cell?.isMerged) continue;
      cells.push({ row, col: colIndex });
    }

    selectedCells.value = expandSelectionForMergeGroups(cells);
    anchorCell.value = cells[0] ?? null;
    activeCell.value = cells[cells.length - 1] ?? null;
  }

  function selectAllCells() {
    if (!activeTable.value) return;

    if (isAllCellsSelected()) {
      clearCellSelection();
      return;
    }

    const cells: SelectedCell[] = [];

    for (const row of activeTable.value.properties.cells) {
      for (const cell of row) {
        if (!cell.isMerged) {
          cells.push({ row: cell.rowIndex, col: cell.colIndex });
        }
      }
    }

    selectedCells.value = cells;
    anchorCell.value = cells[0] ?? null;
    activeCell.value = cells[cells.length - 1] ?? null;
  }

  function insertElementIntoCell(element: FormElement) {
    if (!activeTable.value || !selectedCell.value) return;

    let { row, col } = selectedCell.value;
    const table = activeTable.value;

    const rootCell = getRootCellOfMergeGroup(row, col);
    if (rootCell) {
      row = rootCell.row;
      col = rootCell.col;
    }

    if (!table.properties.cells?.[row]?.[col]) return;

    const updatedCells = [...table.properties.cells];
    updatedCells[row] = [...updatedCells[row]];
    updatedCells[row][col] = {
      ...updatedCells[row][col],
      content: element,
    };

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
      },
    } as Partial<FormElement>);
  }

  function removeCellContent(row: number, col: number) {
    if (!activeTable.value) return;

    const table = activeTable.value;
    if (!table.properties.cells?.[row]?.[col]) return;

    const updatedCells = [...table.properties.cells];
    updatedCells[row] = [...updatedCells[row]];
    updatedCells[row][col] = {
      ...updatedCells[row][col],
      content: undefined,
    };

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
      },
    } as Partial<FormElement>);
  }

  function setCellContent(
    tableId: string,
    row: number,
    col: number,
    content: FormElement | undefined
  ) {
    const element = formBuilderStore.elements.find((candidate) => candidate.id === tableId);
    if (element?.type !== 'TABLE') return;

    const table = element as TableElement;
    if (!table.properties.cells?.[row]?.[col]) return;

    const updatedCells = [...table.properties.cells];
    updatedCells[row] = [...updatedCells[row]];
    updatedCells[row][col] = {
      ...updatedCells[row][col],
      content,
    };

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
      },
    } as Partial<FormElement>);
  }

  function updateCellContent(
    row: number,
    col: number,
    elementUpdates: Partial<FormElement>,
    options?: UpdateElementOptions
  ) {
    if (!activeTable.value) return;

    const table = activeTable.value;
    if (!table.properties.cells?.[row]?.[col]?.content) return;

    const updatedCells = [...table.properties.cells];
    updatedCells[row] = [...updatedCells[row]];

    const currentContent = updatedCells[row][col].content!;
    updatedCells[row][col] = {
      ...updatedCells[row][col],
      content: {
        ...currentContent,
        ...elementUpdates,
        properties: {
          ...currentContent.properties,
          ...elementUpdates.properties,
        },
      },
    };

    formBuilderStore.updateElement(
      table.id,
      {
        properties: {
          ...table.properties,
          cells: updatedCells,
        },
      } as Partial<FormElement>,
      options
    );
  }

  function findCellByElementId(elementId: string): { row: number; col: number } | null {
    if (!activeTable.value) return null;

    const table = activeTable.value;
    for (let row = 0; row < table.properties.cells.length; row++) {
      for (let col = 0; col < table.properties.cells[row].length; col++) {
        if (table.properties.cells[row][col].content?.id === elementId) {
          return { row, col };
        }
      }
    }
    return null;
  }

  function scaleProportionally(sizes: number[], newTotal: number): number[] {
    const currentTotal = sizes.reduce((sum, s) => sum + s, 0);
    const result = sizes.map((s) => Math.floor((s * newTotal) / currentTotal));
    result[result.length - 1] += newTotal - result.reduce((sum, s) => sum + s, 0);
    return result;
  }

  function addRow() {
    if (!activeTable.value) return;

    const table = activeTable.value;

    if (table.properties.rows >= TABLE_CONSTRAINTS.MAX_ROWS) {
      return {
        success: false,
        message: `Limite máximo de ${TABLE_CONSTRAINTS.MAX_ROWS} linhas atingido`,
      };
    }

    const newRowIndex = table.properties.rows;
    const columns = table.properties.columns;

    const newRow: TableCell[] = [];
    for (let col = 0; col < columns; col++) {
      newRow.push({
        id: `${table.id}-cell-${newRowIndex}-${col}`,
        rowIndex: newRowIndex,
        colIndex: col,
        rowSpan: 1,
        colSpan: 1,
        merged: false,
      });
    }

    const updatedCells = [...table.properties.cells, newRow];

    const existingRowHeights = table.properties.rowHeights;
    const updatedRowHeights =
      existingRowHeights?.length === table.properties.rows
        ? [...existingRowHeights, TABLE_CONSTRAINTS.DEFAULT_ROW_HEIGHT]
        : existingRowHeights;

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        rows: table.properties.rows + 1,
        cells: updatedCells,
        rowHeights: updatedRowHeights,
      },
    } as Partial<FormElement>);

    return { success: true };
  }

  function removeRow() {
    if (!activeTable.value) return;

    const table = activeTable.value;
    if (table.properties.rows <= 1) return;

    const updatedCells = table.properties.cells.slice(0, -1);

    const existingRowHeights = table.properties.rowHeights;
    const updatedRowHeights =
      existingRowHeights?.length === table.properties.rows
        ? existingRowHeights.slice(0, -1)
        : existingRowHeights;

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        rows: table.properties.rows - 1,
        cells: updatedCells,
        rowHeights: updatedRowHeights,
      },
    } as Partial<FormElement>);

    selectedCells.value = selectedCells.value.filter(
      (cell) => cell.row < table.properties.rows - 1
    );

    if (anchorCell.value && anchorCell.value.row >= table.properties.rows - 1) {
      anchorCell.value = null;
    }

    if (activeCell.value && activeCell.value.row >= table.properties.rows - 1) {
      activeCell.value = null;
    }

    cleanupOrphanedMergedCells(table.id);
  }

  function addColumn() {
    if (!activeTable.value) return;

    const table = activeTable.value;

    if (table.properties.columns >= TABLE_CONSTRAINTS.MAX_COLUMNS) {
      return {
        success: false,
        message: `Limite máximo de ${TABLE_CONSTRAINTS.MAX_COLUMNS} colunas atingido`,
      };
    }

    const newColIndex = table.properties.columns;

    const updatedCells = table.properties.cells.map((rowCells, rowIndex) => {
      const newCell: TableCell = {
        id: `${table.id}-cell-${rowIndex}-${newColIndex}`,
        rowIndex,
        colIndex: newColIndex,
        rowSpan: 1,
        colSpan: 1,
        merged: false,
      };
      return [...rowCells, newCell];
    });

    const existingColumnWidths = table.properties.columnWidths;
    const newColumnCount = table.properties.columns + 1;
    let updatedColumnWidths: number[] | undefined = existingColumnWidths;
    if (existingColumnWidths?.length === table.properties.columns) {
      const total = existingColumnWidths.reduce((sum, w) => sum + w, 0);
      const newColWidth = Math.floor(total / newColumnCount);
      updatedColumnWidths = [
        ...scaleProportionally(existingColumnWidths, total - newColWidth),
        newColWidth,
      ];
    }

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        columns: newColumnCount,
        cells: updatedCells,
        columnWidths: updatedColumnWidths,
      },
    } as Partial<FormElement>);

    return { success: true };
  }

  function removeColumn() {
    if (!activeTable.value) return;

    const table = activeTable.value;
    if (table.properties.columns <= 1) return;

    const updatedCells = table.properties.cells.map((rowCells) => rowCells.slice(0, -1));

    const existingColumnWidths = table.properties.columnWidths;
    const newColumnCount = table.properties.columns - 1;
    let updatedColumnWidths: number[] | undefined = existingColumnWidths;
    if (existingColumnWidths?.length === table.properties.columns) {
      const total = existingColumnWidths.reduce((sum, w) => sum + w, 0);
      updatedColumnWidths = scaleProportionally(existingColumnWidths.slice(0, -1), total);
    }

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        columns: newColumnCount,
        cells: updatedCells,
        columnWidths: updatedColumnWidths,
      },
    } as Partial<FormElement>);

    selectedCells.value = selectedCells.value.filter(
      (cell) => cell.col < table.properties.columns - 1
    );

    if (anchorCell.value && anchorCell.value.col >= table.properties.columns - 1) {
      anchorCell.value = null;
    }

    if (activeCell.value && activeCell.value.col >= table.properties.columns - 1) {
      activeCell.value = null;
    }

    cleanupOrphanedMergedCells(table.id);
  }

  function buildRowCells(tableId: string, rowIndex: number, columns: number): TableCell[] {
    const row: TableCell[] = [];
    for (let col = 0; col < columns; col++) {
      row.push({
        id: `${tableId}-cell-${rowIndex}-${col}`,
        rowIndex,
        colIndex: col,
        rowSpan: 1,
        colSpan: 1,
        merged: false,
      });
    }
    return row;
  }

  function buildColumnCellsForRow(
    tableId: string,
    rowIndex: number,
    fromColumn: number,
    toColumn: number
  ): TableCell[] {
    const cells: TableCell[] = [];
    for (let col = fromColumn; col < toColumn; col++) {
      cells.push({
        id: `${tableId}-cell-${rowIndex}-${col}`,
        rowIndex,
        colIndex: col,
        rowSpan: 1,
        colSpan: 1,
        merged: false,
      });
    }
    return cells;
  }

  function resizeRowHeights(
    existing: number[] | undefined,
    currentRows: number,
    targetRows: number
  ): number[] | undefined {
    if (existing?.length !== currentRows) return existing;
    if (targetRows > currentRows) {
      return [
        ...existing,
        ...new Array<number>(targetRows - currentRows).fill(TABLE_CONSTRAINTS.DEFAULT_ROW_HEIGHT),
      ];
    }
    return existing.slice(0, targetRows);
  }

  function resizeColumnWidths(
    existing: number[] | undefined,
    currentColumns: number,
    targetColumns: number
  ): number[] | undefined {
    if (existing?.length !== currentColumns) return existing;
    const total = existing.reduce((sum, w) => sum + w, 0);
    if (targetColumns > currentColumns) {
      const addedCount = targetColumns - currentColumns;
      const perNewColumn = Math.floor(total / targetColumns);
      return [
        ...scaleProportionally(existing, total - perNewColumn * addedCount),
        ...new Array<number>(addedCount).fill(perNewColumn),
      ];
    }
    return scaleProportionally(existing.slice(0, targetColumns), total);
  }

  function pruneRowSelection(rowLimit: number) {
    selectedCells.value = selectedCells.value.filter((cell) => cell.row < rowLimit);
    if (anchorCell.value && anchorCell.value.row >= rowLimit) anchorCell.value = null;
    if (activeCell.value && activeCell.value.row >= rowLimit) activeCell.value = null;
  }

  function pruneColumnSelection(columnLimit: number) {
    selectedCells.value = selectedCells.value.filter((cell) => cell.col < columnLimit);
    if (anchorCell.value && anchorCell.value.col >= columnLimit) anchorCell.value = null;
    if (activeCell.value && activeCell.value.col >= columnLimit) activeCell.value = null;
  }

  function setRowCount(targetRows: number) {
    if (!activeTable.value) return;

    const table = activeTable.value;
    const requested = Math.round(targetRows);
    const clamped = Math.min(
      Math.max(requested, TABLE_CONSTRAINTS.MIN_ROWS),
      TABLE_CONSTRAINTS.MAX_ROWS
    );
    const rowLimitResult =
      requested > TABLE_CONSTRAINTS.MAX_ROWS
        ? { success: false, message: `Limite máximo de ${TABLE_CONSTRAINTS.MAX_ROWS} linhas atingido` }
        : { success: true };

    const currentRows = table.properties.rows;
    if (clamped === currentRows) {
      return rowLimitResult;
    }

    const growing = clamped > currentRows;
    let updatedCells: TableCell[][];
    if (growing) {
      const appendedRows: TableCell[][] = [];
      for (let row = currentRows; row < clamped; row++) {
        appendedRows.push(buildRowCells(table.id, row, table.properties.columns));
      }
      updatedCells = [...table.properties.cells, ...appendedRows];
    } else {
      updatedCells = table.properties.cells.slice(0, clamped);
    }

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        rows: clamped,
        cells: updatedCells,
        rowHeights: resizeRowHeights(table.properties.rowHeights, currentRows, clamped),
      },
    } as Partial<FormElement>);

    if (!growing) {
      pruneRowSelection(clamped);
      cleanupOrphanedMergedCells(table.id);
    }

    return rowLimitResult;
  }

  function setColumnCount(targetColumns: number) {
    if (!activeTable.value) return;

    const table = activeTable.value;
    const requested = Math.round(targetColumns);
    const clamped = Math.min(
      Math.max(requested, TABLE_CONSTRAINTS.MIN_COLUMNS),
      TABLE_CONSTRAINTS.MAX_COLUMNS
    );
    const columnLimitResult =
      requested > TABLE_CONSTRAINTS.MAX_COLUMNS
        ? { success: false, message: `Limite máximo de ${TABLE_CONSTRAINTS.MAX_COLUMNS} colunas atingido` }
        : { success: true };

    const currentColumns = table.properties.columns;
    if (clamped === currentColumns) {
      return columnLimitResult;
    }

    const growing = clamped > currentColumns;
    const updatedCells = table.properties.cells.map((rowCells, rowIndex) =>
      growing
        ? [...rowCells, ...buildColumnCellsForRow(table.id, rowIndex, currentColumns, clamped)]
        : rowCells.slice(0, clamped)
    );

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        columns: clamped,
        cells: updatedCells,
        columnWidths: resizeColumnWidths(table.properties.columnWidths, currentColumns, clamped),
      },
    } as Partial<FormElement>);

    if (!growing) {
      pruneColumnSelection(clamped);
      cleanupOrphanedMergedCells(table.id);
    }

    return columnLimitResult;
  }

  function updateCellProperty<K extends keyof CellProperties>(
    property: K,
    value: CellProperties[K]
  ) {
    if (!activeTable.value || !selectedCell.value) return;

    const { row, col } = selectedCell.value;
    const table = activeTable.value;

    if (!table.properties.cells?.[row]?.[col]) return;

    const updatedCells = [...table.properties.cells];
    updatedCells[row] = [...updatedCells[row]];
    updatedCells[row][col] = {
      ...updatedCells[row][col],
      properties: {
        ...updatedCells[row][col].properties,
        [property]: value,
      },
    };

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
      },
    } as Partial<FormElement>);
  }

  function updateSelectedCellsProperty<K extends keyof CellProperties>(
    property: K,
    value: CellProperties[K]
  ) {
    if (!activeTable.value || selectedCells.value.length === 0) return;

    const table = activeTable.value;
    const updatedCells = [...table.properties.cells];
    let hasChanges = false;

    selectedCells.value.forEach(({ row, col }) => {
      if (!updatedCells[row]?.[col]) return;
      updatedCells[row] = [...updatedCells[row]];
      updatedCells[row][col] = {
        ...updatedCells[row][col],
        properties: {
          ...updatedCells[row][col].properties,
          [property]: value,
        },
      };
      hasChanges = true;
    });

    if (hasChanges) {
      formBuilderStore.updateElement(table.id, {
        properties: {
          ...table.properties,
          cells: updatedCells,
        },
      } as Partial<FormElement>);
    }
  }

  function updateCellBorder(side: BorderSideType, properties: Partial<BorderSide>) {
    if (!activeTable.value || !selectedCell.value) return;

    const { row, col } = selectedCell.value;
    const table = activeTable.value;

    if (!table.properties.cells?.[row]?.[col]) return;

    const isRemoving = properties.style === 'none' || properties.width === 0;
    const currentSide = table.properties.cells[row][col].properties?.border?.[side];
    const value: BorderSide | undefined = isRemoving
      ? undefined
      : { ...currentSide, ...properties };

    const updatedCells = table.properties.cells.map((rowCells) => [...rowCells]);

    applyBorderEdit(
      updatedCells,
      row,
      col,
      side,
      value,
      table.properties.mergedCells ?? [],
      table.properties.rows,
      table.properties.columns
    );

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
      },
    } as Partial<FormElement>);
  }

  function removeCellBorders() {
    if (!activeTable.value || !selectedCell.value) return;

    const { row, col } = selectedCell.value;
    const table = activeTable.value;

    if (!table.properties.cells?.[row]?.[col]) return;

    const updatedCells = table.properties.cells.map((rowCells) => [...rowCells]);
    const mergedCells = table.properties.mergedCells ?? [];

    for (const side of ALL_SIDES) {
      applyBorderEdit(
        updatedCells,
        row,
        col,
        side,
        undefined,
        mergedCells,
        table.properties.rows,
        table.properties.columns
      );
    }

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
      },
    } as Partial<FormElement>);
  }

  type BorderPattern =
    | 'all'
    | 'top'
    | 'right'
    | 'bottom'
    | 'left'
    | 'top-bottom'
    | 'left-right'
    | 'none';

  type BorderStyleValue = 'none' | 'solid' | 'dashed' | 'dotted';

  function updateCellBordersPattern(
    pattern: BorderPattern,
    properties: { color: string; width: number; style: BorderStyleValue }
  ) {
    if (!activeTable.value || selectedCells.value.length === 0) return;

    const table = activeTable.value;
    const mergedCells = table.properties.mergedCells ?? [];
    const totalRows = table.properties.rows;
    const totalCols = table.properties.columns;

    const sideValues: Record<BorderSideType, BorderSide | undefined> = {
      top: undefined,
      right: undefined,
      bottom: undefined,
      left: undefined,
    };

    if (pattern !== 'none') {
      const value: BorderSide = {
        width: properties.width,
        color: properties.color,
        style: properties.style,
      };

      const sidesInPattern: BorderSideType[] = [];
      switch (pattern) {
        case 'all':
          sidesInPattern.push('top', 'right', 'bottom', 'left');
          break;
        case 'top':
          sidesInPattern.push('top');
          break;
        case 'right':
          sidesInPattern.push('right');
          break;
        case 'bottom':
          sidesInPattern.push('bottom');
          break;
        case 'left':
          sidesInPattern.push('left');
          break;
        case 'top-bottom':
          sidesInPattern.push('top', 'bottom');
          break;
        case 'left-right':
          sidesInPattern.push('left', 'right');
          break;
      }

      sidesInPattern.forEach((side) => {
        sideValues[side] = value;
      });
    }

    const updatedCells = table.properties.cells.map((rowCells) => [...rowCells]);

    const excludeMirrorKeys = new Set<string>();
    selectedCells.value.forEach(({ row, col }) => {
      if (!updatedCells[row]?.[col]) return;
      const fp = getCellFootprint(updatedCells, row, col, mergedCells);
      for (let r = fp.startRow; r <= fp.endRow; r++) {
        for (let c = fp.startCol; c <= fp.endCol; c++) {
          excludeMirrorKeys.add(`${r},${c}`);
        }
      }
    });

    selectedCells.value.forEach(({ row, col }) => {
      if (!updatedCells[row]?.[col]) return;
      for (const side of ALL_SIDES) {
        applyBorderEdit(
          updatedCells,
          row,
          col,
          side,
          sideValues[side],
          mergedCells,
          totalRows,
          totalCols,
          excludeMirrorKeys
        );
      }
    });

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
      },
    } as Partial<FormElement>);
  }

  function updateTableColumnWidths(tableId: string, columnWidths: number[]) {
    const table = formBuilderStore.elements.find((el) => el.id === tableId);
    if (!table || table.type !== 'TABLE') return;

    formBuilderStore.updateElement(tableId, {
      properties: {
        ...table.properties,
        columnWidths,
      },
    } as Partial<FormElement>);
  }

  function updateTableRowHeights(tableId: string, rowHeights: number[]) {
    const table = formBuilderStore.elements.find((el) => el.id === tableId);
    if (!table || table.type !== 'TABLE') return;

    formBuilderStore.updateElement(tableId, {
      properties: {
        ...table.properties,
        rowHeights,
      },
    } as Partial<FormElement>);
  }

  function mergeCells(cells: SelectedCell[]): void {
    if (!activeTable.value || cells.length < 2) return;

    const minRow = Math.min(...cells.map((c) => c.row));
    const maxRow = Math.max(...cells.map((c) => c.row));
    const minCol = Math.min(...cells.map((c) => c.col));
    const maxCol = Math.max(...cells.map((c) => c.col));

    const table = activeTable.value;
    const mergeGroupId = `merge-${Date.now()}`;

    // Collect all existing merge group IDs in the selection that need to be dissolved
    const mergeGroupsToDissolve = new Set<string>();
    for (const cell of cells) {
      const cellData = table.properties.cells[cell.row]?.[cell.col];
      if (cellData?.mergeGroupId) {
        mergeGroupsToDissolve.add(cellData.mergeGroupId);
      }
    }

    const rootCell = table.properties.cells[minRow]?.[minCol];
    const cloneProperties = (props: TableCell['properties']): TableCell['properties'] =>
      props ? (JSON.parse(JSON.stringify(props)) as TableCell['properties']) : undefined;

    const contentSourceCell =
      getFirstNonEmptyCell(table.properties.cells, { minRow, maxRow, minCol, maxCol }) ?? rootCell;

    const preservedProperties = cloneProperties(contentSourceCell?.properties);
    const preservedContent = contentSourceCell?.content;

    const cellIds: string[] = [];
    const updatedCells = table.properties.cells.map((rowCells, rowIndex) =>
      rowCells.map((cell, colIndex) => {
        if (rowIndex >= minRow && rowIndex <= maxRow && colIndex >= minCol && colIndex <= maxCol) {
          cellIds.push(cell.id);
          const isRoot = rowIndex === minRow && colIndex === minCol;
          return {
            ...cell,
            isMerged: !isRoot,
            mergeGroupId,
            rowSpan: isRoot ? maxRow - minRow + 1 : 0,
            colSpan: isRoot ? maxCol - minCol + 1 : 0,
            properties: cloneProperties(preservedProperties),
            content: isRoot ? preservedContent : undefined,
          };
        }
        return cell;
      })
    );

    const mergedCell = {
      id: mergeGroupId,
      startRow: minRow,
      startCol: minCol,
      endRow: maxRow,
      endCol: maxCol,
      cells: cellIds,
    };

    const existingMergedCells = table.properties.mergedCells ?? [];

    // Filter out old merge groups that are being dissolved and add the new one
    const updatedMergedCells = existingMergedCells.filter((m) => !mergeGroupsToDissolve.has(m.id));

    const mergeFootprint: CellFootprint = {
      startRow: minRow,
      endRow: maxRow,
      startCol: minCol,
      endCol: maxCol,
    };
    const totalRows = updatedCells.length;
    const totalCols = updatedCells[0]?.length ?? 0;

    for (const side of ALL_SIDES) {
      const value = preservedProperties?.border?.[side];
      const oppSide = oppositeSide[side];
      const neighborFps = getEdgeNeighborFootprints(
        updatedCells,
        mergeFootprint,
        side,
        totalRows,
        totalCols,
        updatedMergedCells
      );
      for (const fp of neighborFps) {
        writeFootprintSide(updatedCells, fp, oppSide, value);
      }
    }

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
        mergedCells: [...updatedMergedCells, mergedCell],
      },
    } as Partial<FormElement>);

    selectedCells.value = [{ row: minRow, col: minCol }];
  }

  function cleanupOrphanedMergedCells(tableId: string): void {
    const element = formBuilderStore.elements.find((el) => el.id === tableId);
    if (!element || element.type !== 'TABLE') return;

    const table = element as TableElement;
    const mergedCells = table.properties.mergedCells ?? [];
    if (mergedCells.length === 0) return;

    const validCellIds = new Set<string>();
    table.properties.cells.forEach((rowCells: TableCell[]) => {
      rowCells.forEach((cell: TableCell) => {
        validCellIds.add(cell.id);
      });
    });

    const cleanedMergedCells = mergedCells.filter((mergeRecord) => {
      const allCellsExist = mergeRecord.cells.every((cellId: string) => validCellIds.has(cellId));
      return allCellsExist;
    });

    if (cleanedMergedCells.length !== mergedCells.length) {
      const orphanedGroupIds = new Set(
        mergedCells
          .filter((m) => !cleanedMergedCells.some((c) => c.id === m.id))
          .map((m) => m.id)
      );

      const updatedCells = table.properties.cells.map((rowCells: TableCell[]) =>
        rowCells.map((cell: TableCell) => {
          if (cell.mergeGroupId && orphanedGroupIds.has(cell.mergeGroupId)) {
            return { ...cell, colSpan: 1, rowSpan: 1, isMerged: false, mergeGroupId: undefined };
          }
          return cell;
        })
      );

      formBuilderStore.updateElement(
        tableId,
        {
          properties: {
            ...table.properties,
            cells: updatedCells,
            mergedCells: cleanedMergedCells,
          },
        } as Partial<FormElement>,
        { trackHistory: false }
      );
    }
  }

  function insertRowAt(index: number, position: 'above' | 'below') {
    if (!activeTable.value) return;

    const table = activeTable.value;
    if (table.properties.rows >= TABLE_CONSTRAINTS.MAX_ROWS) {
      return {
        success: false,
        message: `Limite máximo de ${TABLE_CONSTRAINTS.MAX_ROWS} linhas atingido`,
      };
    }

    const insertAt = position === 'above' ? index : index + 1;
    const columns = table.properties.columns;

    const newRow: TableCell[] = [];
    for (let col = 0; col < columns; col++) {
      newRow.push({
        id: `${table.id}-cell-${Date.now()}-${col}`,
        rowIndex: insertAt,
        colIndex: col,
        rowSpan: 1,
        colSpan: 1,
        merged: false,
      });
    }

    const updatedCells = [...table.properties.cells];
    updatedCells.splice(insertAt, 0, newRow);

    for (let row = 0; row < updatedCells.length; row++) {
      updatedCells[row] = updatedCells[row].map((cell) => ({
        ...cell,
        rowIndex: row,
      }));
    }

    const updatedMergedCells = (table.properties.mergedCells ?? []).map((m) => {
      const updated = { ...m };
      if (m.startRow >= insertAt) {
        updated.startRow = m.startRow + 1;
      }
      if (m.endRow >= insertAt) {
        updated.endRow = m.endRow + 1;
      }
      return updated;
    });

    const updatedRowHeights = table.properties.rowHeights
      ? [
          ...table.properties.rowHeights.slice(0, insertAt),
          26,
          ...table.properties.rowHeights.slice(insertAt),
        ]
      : undefined;

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        rows: table.properties.rows + 1,
        cells: updatedCells,
        mergedCells: updatedMergedCells,
        ...(updatedRowHeights ? { rowHeights: updatedRowHeights } : {}),
      },
    } as Partial<FormElement>);

    clearCellSelection();
    return { success: true };
  }

  function insertColumnAt(index: number, position: 'left' | 'right') {
    if (!activeTable.value) return;

    const table = activeTable.value;
    if (table.properties.columns >= TABLE_CONSTRAINTS.MAX_COLUMNS) {
      return {
        success: false,
        message: `Limite máximo de ${TABLE_CONSTRAINTS.MAX_COLUMNS} colunas atingido`,
      };
    }

    const insertAt = position === 'left' ? index : index + 1;

    const updatedCells = table.properties.cells.map((rowCells, rowIndex) => {
      const newCell: TableCell = {
        id: `${table.id}-cell-${rowIndex}-${Date.now()}-${insertAt}`,
        rowIndex,
        colIndex: insertAt,
        rowSpan: 1,
        colSpan: 1,
        merged: false,
      };
      const row = [...rowCells];
      row.splice(insertAt, 0, newCell);
      return row.map((cell, colIdx) => ({ ...cell, colIndex: colIdx }));
    });

    const updatedMergedCells = (table.properties.mergedCells ?? []).map((m) => {
      const updated = { ...m };
      if (m.startCol >= insertAt) {
        updated.startCol = m.startCol + 1;
      }
      if (m.endCol >= insertAt) {
        updated.endCol = m.endCol + 1;
      }
      return updated;
    });

    const updatedColumnWidths = table.properties.columnWidths
      ? [
          ...table.properties.columnWidths.slice(0, insertAt),
          table.properties.columnWidths[
            Math.min(insertAt, table.properties.columnWidths.length - 1)
          ],
          ...table.properties.columnWidths.slice(insertAt),
        ]
      : undefined;

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        columns: table.properties.columns + 1,
        cells: updatedCells,
        mergedCells: updatedMergedCells,
        ...(updatedColumnWidths ? { columnWidths: updatedColumnWidths } : {}),
      },
    } as Partial<FormElement>);

    clearCellSelection();
    return { success: true };
  }

  function deleteRowAt(index: number) {
    if (!activeTable.value) return;

    const table = activeTable.value;
    if (table.properties.rows <= 1) return;

    const updatedCells = table.properties.cells.filter((_, rowIdx) => rowIdx !== index);
    for (let row = 0; row < updatedCells.length; row++) {
      updatedCells[row] = updatedCells[row].map((cell) => ({
        ...cell,
        rowIndex: row,
      }));
    }

    const updatedMergedCells = (table.properties.mergedCells ?? [])
      .filter((m) => !(m.startRow === index && m.endRow === index))
      .map((m) => {
        const updated = { ...m };
        if (m.startRow > index) updated.startRow = m.startRow - 1;
        if (m.endRow > index) updated.endRow = m.endRow - 1;
        if (m.startRow <= index && m.endRow > index) {
          updated.endRow = m.endRow - 1;
        }
        return updated;
      })
      .filter((m) => m.startRow <= m.endRow);

    const updatedRowHeights = table.properties.rowHeights
      ? table.properties.rowHeights.filter((_, i) => i !== index)
      : undefined;

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        rows: table.properties.rows - 1,
        cells: updatedCells,
        mergedCells: updatedMergedCells,
        ...(updatedRowHeights ? { rowHeights: updatedRowHeights } : {}),
      },
    } as Partial<FormElement>);

    clearCellSelection();
    cleanupOrphanedMergedCells(table.id);
  }

  function deleteColumnAt(index: number) {
    if (!activeTable.value) return;

    const table = activeTable.value;
    if (table.properties.columns <= 1) return;

    const updatedCells = table.properties.cells.map((rowCells) => {
      const row = rowCells.filter((_, colIdx) => colIdx !== index);
      return row.map((cell, colIdx) => ({ ...cell, colIndex: colIdx }));
    });

    const updatedMergedCells = (table.properties.mergedCells ?? [])
      .filter((m) => !(m.startCol === index && m.endCol === index))
      .map((m) => {
        const updated = { ...m };
        if (m.startCol > index) updated.startCol = m.startCol - 1;
        if (m.endCol > index) updated.endCol = m.endCol - 1;
        if (m.startCol <= index && m.endCol > index) {
          updated.endCol = m.endCol - 1;
        }
        return updated;
      })
      .filter((m) => m.startCol <= m.endCol);

    const updatedColumnWidths = table.properties.columnWidths
      ? table.properties.columnWidths.filter((_, i) => i !== index)
      : undefined;

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        columns: table.properties.columns - 1,
        cells: updatedCells,
        mergedCells: updatedMergedCells,
        ...(updatedColumnWidths ? { columnWidths: updatedColumnWidths } : {}),
      },
    } as Partial<FormElement>);

    clearCellSelection();
    cleanupOrphanedMergedCells(table.id);
  }

  function deleteTable() {
    if (!activeTable.value) return;

    const tableId = activeTable.value.id;
    exitEditingMode();
    formBuilderStore.removeElement(tableId);
  }

  function clearSelectedCellsContentAndProperties() {
    if (!activeTable.value || selectedCells.value.length === 0) return;

    const table = activeTable.value;
    const updatedCells = [...table.properties.cells];
    const clonedRows = new Set<number>();
    let hasChanges = false;

    selectedCells.value.forEach(({ row, col }) => {
      const targetCell = updatedCells[row]?.[col];
      if (!targetCell) return;

      if (!clonedRows.has(row)) {
        updatedCells[row] = [...updatedCells[row]];
        clonedRows.add(row);
      }

      updatedCells[row][col] = {
        ...targetCell,
        content: undefined,
        properties: undefined,
      };
      hasChanges = true;
    });

    if (!hasChanges) return;

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
      },
    } as Partial<FormElement>);
  }

  function clearSelectedCellsContent() {
    if (!activeTable.value || selectedCells.value.length === 0) return;

    const table = activeTable.value;
    const updatedCells = [...table.properties.cells];
    const clonedRows = new Set<number>();
    let hasChanges = false;

    selectedCells.value.forEach(({ row, col }) => {
      const targetCell = updatedCells[row]?.[col];
      if (!targetCell?.content) return;

      if (!clonedRows.has(row)) {
        updatedCells[row] = [...updatedCells[row]];
        clonedRows.add(row);
      }

      updatedCells[row][col] = {
        ...targetCell,
        content: undefined,
      };
      hasChanges = true;
    });

    if (!hasChanges) return;

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
      },
    } as Partial<FormElement>);
  }

  function updateRowCellsProperty<K extends keyof CellProperties>(
    rowIndex: number,
    property: K,
    value: CellProperties[K]
  ) {
    if (!activeTable.value) return;

    const table = activeTable.value;
    const updatedCells = [...table.properties.cells];
    updatedCells[rowIndex] = updatedCells[rowIndex].map((cell) => ({
      ...cell,
      properties: {
        ...cell.properties,
        [property]: value,
      },
    }));

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
      },
    } as Partial<FormElement>);
  }

  function updateColumnCellsProperty<K extends keyof CellProperties>(
    colIndex: number,
    property: K,
    value: CellProperties[K]
  ) {
    if (!activeTable.value) return;

    const table = activeTable.value;
    const updatedCells = table.properties.cells.map((rowCells) =>
      rowCells.map((cell, idx) => {
        if (idx !== colIndex) return cell;
        return {
          ...cell,
          properties: {
            ...cell.properties,
            [property]: value,
          },
        };
      })
    );

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
      },
    } as Partial<FormElement>);
  }

  function updateAllCellsProperty<K extends keyof CellProperties>(
    property: K,
    value: CellProperties[K]
  ) {
    if (!activeTable.value) return;

    const table = activeTable.value;
    const updatedCells = table.properties.cells.map((rowCells) =>
      rowCells.map((cell) => ({
        ...cell,
        properties: {
          ...cell.properties,
          [property]: value,
        },
      }))
    );

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
      },
    } as Partial<FormElement>);
  }

  function updateRowBordersPattern(
    rowIndex: number,
    pattern: BorderPattern,
    properties: { color: string; width: number; style: 'none' | 'solid' | 'dashed' | 'dotted' }
  ) {
    if (!activeTable.value) return;

    const table = activeTable.value;
    const columns = table.properties.columns;
    const cells: SelectedCell[] = [];
    for (let col = 0; col < columns; col++) {
      cells.push({ row: rowIndex, col });
    }

    const savedSelection = [...selectedCells.value];
    selectedCells.value = cells;
    updateCellBordersPattern(pattern, properties);
    selectedCells.value = savedSelection;
  }

  function updateColumnBordersPattern(
    colIndex: number,
    pattern: BorderPattern,
    properties: { color: string; width: number; style: 'none' | 'solid' | 'dashed' | 'dotted' }
  ) {
    if (!activeTable.value) return;

    const table = activeTable.value;
    const rows = table.properties.rows;
    const cells: SelectedCell[] = [];
    for (let row = 0; row < rows; row++) {
      cells.push({ row, col: colIndex });
    }

    const savedSelection = [...selectedCells.value];
    selectedCells.value = cells;
    updateCellBordersPattern(pattern, properties);
    selectedCells.value = savedSelection;
  }

  function unmergeCells(mergeGroupId: string): void {
    if (!activeTable.value) return;

    const table = activeTable.value;
    const mergedCells = table.properties.mergedCells ?? [];
    const mergedCell = mergedCells.find((m) => m.id === mergeGroupId);

    if (!mergedCell) return;

    const updatedCells = table.properties.cells.map((rowCells) =>
      rowCells.map((cell) => {
        if (cell.mergeGroupId === mergeGroupId) {
          return {
            ...cell,
            isMerged: false,
            mergeGroupId: undefined,
            rowSpan: 1,
            colSpan: 1,
          };
        }
        return cell;
      })
    );

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
        mergedCells: mergedCells.filter((m) => m.id !== mergeGroupId),
      },
    } as Partial<FormElement>);

    clearCellSelection();
  }

  function unmergeMultipleCells(mergeGroupIds: string[]): void {
    if (!activeTable.value || mergeGroupIds.length === 0) return;

    const table = activeTable.value;
    const mergedCells = table.properties.mergedCells ?? [];
    const uniqueIds = new Set(mergeGroupIds);
    const existingIds = new Set(
      mergedCells.filter((m) => uniqueIds.has(m.id)).map((m) => m.id)
    );
    if (existingIds.size === 0) return;

    const updatedCells = table.properties.cells.map((rowCells) =>
      rowCells.map((cell) => {
        if (cell.mergeGroupId && existingIds.has(cell.mergeGroupId)) {
          return { ...cell, isMerged: false, mergeGroupId: undefined, rowSpan: 1, colSpan: 1 };
        }
        return cell;
      })
    );

    formBuilderStore.updateElement(table.id, {
      properties: {
        ...table.properties,
        cells: updatedCells,
        mergedCells: mergedCells.filter((m) => !existingIds.has(m.id)),
      },
    } as Partial<FormElement>);

    clearCellSelection();
  }

  return {
    activeTableId: readonly(activeTableId),
    selectedCell: readonly(selectedCell),
    selectedCells: readonly(selectedCells),
    anchorCell: readonly(anchorCell),
    activeCell: readonly(activeCell),
    isEditingMode,
    activeTable,
    selectedCellData,
    selectedCellsData,
    hasMultipleSelection,
    enterEditingMode,
    exitEditingMode,
    selectCell,
    selectCellRange,
    selectRow,
    selectColumn,
    selectAllCells,
    clearCellSelection,
    insertElementIntoCell,
    removeCellContent,
    setCellContent,
    updateCellContent,
    findCellByElementId,
    addRow,
    removeRow,
    addColumn,
    removeColumn,
    setRowCount,
    setColumnCount,
    insertRowAt,
    insertColumnAt,
    deleteRowAt,
    deleteColumnAt,
    deleteTable,
    clearSelectedCellsContentAndProperties,
    clearSelectedCellsContent,
    updateCellProperty,
    updateSelectedCellsProperty,
    updateCellBorder,
    removeCellBorders,
    updateCellBordersPattern,
    updateRowCellsProperty,
    updateColumnCellsProperty,
    updateAllCellsProperty,
    updateRowBordersPattern,
    updateColumnBordersPattern,
    updateTableColumnWidths,
    updateTableRowHeights,
    mergeCells,
    unmergeCells,
    unmergeMultipleCells,
    cleanupOrphanedMergedCells,
  };
});
